/**
 * 跨平台 skill 装载逻辑（可编译版，供 postinstall 与 `baskreport install` 共用）。
 *
 * 与 scripts/install-skill.ts 等价，但不依赖 tsx / devDependencies，由 tsup 编译进 dist/。
 *
 * 客户端挂载策略：
 *   - codebuddy / workbuddy：用户级目录链接（junction / symlink）装到 ~/.{client}/skills/<name>
 *   - claude / cursor：项目级文件（.claude/commands、.cursor/rules），需在使用者自己的项目里生成，
 *     故 postinstall 默认只挂 codebuddy/workbuddy；项目级由使用者手动 `baskreport install` 触发。
 *
 * 用法:
 *   node dist/install.js [--clients codebuddy,workbuddy] [--project-root <dir>] [--build]
 *   baskreport install [--clients claude,cursor] [--project-root <dir>]
 */
import { execSync } from "node:child_process";
import * as fs from "node:fs";
import * as path from "node:path";
import * as os from "node:os";
import { fileURLToPath } from "node:url";

interface Options {
  build: boolean;
  clients: Client[];
  projectRoot?: string;
}

export const KNOWN_CLIENTS = ["codebuddy", "workbuddy", "gemini", "codex", "claude", "cursor", "generic"] as const;
export type Client = (typeof KNOWN_CLIENTS)[number];

export function expandClients(raw: string[]): Client[] {
  const set = new Set<Client>();
  for (const c of raw) {
    if (c === "all") {
      KNOWN_CLIENTS.forEach((k) => set.add(k));
    } else if ((KNOWN_CLIENTS as readonly string[]).includes(c)) {
      set.add(c as Client);
    } else {
      throw new Error(`未知客户端: ${c}。支持: ${KNOWN_CLIENTS.join(", ")} 或 all`);
    }
  }
  return [...set];
}

export function parseArgs(argv: string[]): Options {
  const options: Options = { build: false, clients: ["codebuddy", "workbuddy"] };
  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (!arg) continue;
    if (arg === "--build") {
      options.build = true;
    } else if (arg === "--clients") {
      const val = argv[++i];
      if (!val) throw new Error("--clients 需要一个逗号分隔的客户端列表");
      options.clients = expandClients(val.split(",").map((c) => c.trim()).filter(Boolean));
    } else if (arg.startsWith("--clients=")) {
      options.clients = expandClients(
        arg.slice("--clients=".length).split(",").map((c) => c.trim()).filter(Boolean),
      );
    } else if (arg === "--project-root") {
      const val = argv[++i];
      if (!val) throw new Error("--project-root 需要一个目录路径");
      options.projectRoot = val;
    } else if (arg.startsWith("--project-root=")) {
      options.projectRoot = arg.slice("--project-root=".length);
    } else {
      throw new Error(`未知参数: ${arg}`);
    }
  }
  return options;
}

function isLink(targetPath: string): boolean {
  try {
    return fs.lstatSync(targetPath).isSymbolicLink();
  } catch {
    return false;
  }
}

function readSkillMeta(skillMd: string): { description: string; body: string } {
  const raw = fs.readFileSync(skillMd, "utf8");
  const fm = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?/);
  let description = "";
  let body = raw;
  if (fm) {
    const fmText = fm[1] ?? "";
    const m = fmText.match(/^\s*description:\s*([\s\S]*?)(?:\n\w|$)/m);
    if (m && m[1]) description = m[1].trim();
    body = raw.slice(fm[0].length);
  }
  return { description, body: body.replace(/^\n+/, "") };
}

function genClaudeCommand(projectRoot: string, body: string): void {
  const dir = path.join(projectRoot, ".claude", "commands");
  fs.mkdirSync(dir, { recursive: true });
  const out = path.join(dir, "baskreport.md");
  const header = [
    "<!--",
    "此文件由 baskreport install 自动生成；如需修改请改 SKILL.md 后重跑。",
    "在 Claude Code 中用 /baskreport 调用。",
    "-->",
    "",
  ].join("\n");
  fs.writeFileSync(out, header + body, "utf8");
  console.log(`  已生成: ${out}`);
}

function genCursorRule(projectRoot: string, description: string, body: string): void {
  const dir = path.join(projectRoot, ".cursor", "rules");
  fs.mkdirSync(dir, { recursive: true });
  const out = path.join(dir, "basksoft-ai.mdc");
  const fm = [
    "---",
    `description: ${description}`,
    "alwaysApply: false",
    "globs:",
    "  - '**/*.json'",
    "  - '**/*.xml'",
    "---",
    "",
  ].join("\n");
  const header = [
    "<!--",
    "此文件由 baskreport install 自动生成；如需修改请改 SKILL.md 后重跑。",
    "-->",
    "",
  ].join("\n");
  fs.writeFileSync(out, fm + header + body, "utf8");
  console.log(`  已生成: ${out}`);
}

/**
 * 为 generic（未配置的第三方 agent）生成通用 skill 说明文件（项目级 baskreport.md）。
 *
 * 第三方 agent 没有统一的能力目录规范，故不采用目录链接，而是把 SKILL.md 正文 + 使用指引
 * 落到项目内一份独立的说明文件中，交由使用者按第三方 agent 自己的规范加载：
 *   - 复制为系统提示 / 附加上下文 / MCP 工具描述等；
 *   - 文件本身就是 SKILL.md 的可分发快照，agent 只需读它 + 能执行 CLI 即可使用。
 */
function genGenericSkill(projectRoot: string, body: string): void {
  fs.mkdirSync(projectRoot, { recursive: true }); // 目标目录可能不存在（--project-root 指向新目录）
  const out = path.join(projectRoot, "baskreport.md");
  const header = [
    "<!--",
    "此文件由 baskreport install --clients generic 自动生成；如需修改请改 SKILL.md 后重跑。",
    "面向未配置的第三方 agent（如 Gemini CLI、Copilot、自研 agent 等）。",
    "用法：把本文件作为该 agent 的 skill / 附加上下文 / 系统提示加载，",
    "并确保 agent 能执行 CLI（全局 `baskreport` 或 `npx baskreport-ai-report-generator`）。",
    "-->",
    "",
  ].join("\n");
  fs.writeFileSync(out, header + body, "utf8");
  console.log(`  已生成: ${out}`);
}

/**
 * 为 codex（OpenAI Codex CLI）生成项目级指令文件 AGENTS.md。
 *
 * Codex CLI 官方支持项目根 AGENTS.md 作为自动加载的项目指令文件（类似 CLAUDE.md/.cursorrules）。
 * 注意：codex 没有统一的用户级 skills 目录规范，故采用项目级 AGENTS.md 作为最稳妥的装载方式。
 * 生成时把 SKILL.md 正文写入，Codex 打开项目即自动注入上下文。
 */
function genAgentsMd(projectRoot: string, body: string): void {
  fs.mkdirSync(projectRoot, { recursive: true });
  const out = path.join(projectRoot, "AGENTS.md");
  const header = [
    "<!--",
    "此文件由 baskreport install --clients codex 自动生成；如需修改请改 SKILL.md 后重跑。",
    "OpenAI Codex CLI 会自动加载项目根的 AGENTS.md；其中包含 BaskReport 报表生成技能说明。",
    "-->",
    "",
  ].join("\n");
  fs.writeFileSync(out, header + body, "utf8");
  console.log(`  已生成: ${out}`);
}

export function runInstall(opts: Options, skillRoot: string): void {
  const skillMd = path.join(skillRoot, "SKILL.md");
  if (!fs.existsSync(skillMd)) {
    throw new Error(`未找到 SKILL.md，请确认运行目录正确。skillRoot=${skillRoot}`);
  }

  const skillName = path.basename(skillRoot);
  console.log(`Skill 名称: ${skillName}`);
  console.log(`仓库路径:   ${skillRoot}`);
  console.log(`目标客户端: ${opts.clients.join(", ")}`);

  if (opts.build) {
    console.log("\n[1/3] 构建产物...");
    execSync("npm install && npm run build", { cwd: skillRoot, stdio: "inherit" });
  } else {
    console.log("\n[1/3] 跳过构建");
  }

  const { description, body } = readSkillMeta(skillMd);

  console.log("[2/3] 创建客户端链接 / 生成命令与规则...");
  const homeDir = os.homedir();

  for (const client of opts.clients) {
    if (client === "claude") {
      const root = opts.projectRoot ?? skillRoot;
      genClaudeCommand(root, body);
      continue;
    }
    if (client === "cursor") {
      const root = opts.projectRoot ?? skillRoot;
      genCursorRule(root, description, body);
      continue;
    }
    if (client === "generic") {
      const root = opts.projectRoot ?? skillRoot;
      genGenericSkill(root, body);
      continue;
    }
    if (client === "codex") {
      const root = opts.projectRoot ?? skillRoot;
      genAgentsMd(root, body);
      continue;
    }

    // codebuddy / workbuddy / gemini：用户级目录链接（~/.{client}/skills）
    const skillsDir = path.join(homeDir, `.${client}`, "skills");
    const linkPath = path.join(skillsDir, skillName);
    fs.mkdirSync(skillsDir, { recursive: true });

    if (fs.existsSync(linkPath)) {
      if (isLink(linkPath)) {
        console.log(`  移除旧链接: ${linkPath}`);
        fs.rmSync(linkPath, { force: true, recursive: true });
      } else {
        throw new Error(
          `目标已存在且为真实目录，已中止以免误删数据: ${linkPath}\n请手动处理后再运行。`,
        );
      }
    }

    const type = process.platform === "win32" ? "junction" : "dir";
    fs.symlinkSync(skillRoot, linkPath, type);
    console.log(`  已创建: ${linkPath} -> ${skillRoot}`);
  }

  console.log("\n[3/3] 完成。");
  console.log(`  - codebuddy / workbuddy / gemini：在对应客户端重新加载即可使用 skill '${skillName}'。`);
  console.log(`  - claude：用 /baskreport 触发（命令文件位于 .claude/commands/baskreport.md）。`);
  console.log(`  - codex：打开项目后 Codex CLI 会自动加载 AGENTS.md（含 BaskReport 技能说明）。`);
  console.log(`  - cursor：打开项目后提及 BaskReport 报表需求会自动加载 .cursor/rules/basksoft-ai.mdc。`);
  console.log(`  - generic：把项目内 baskreport.md 交给第三方 agent 加载（详见该文件头部指引）。`);
}

/**
 * 卸载 skill：对称地移除 install 创建的内容。
 *
 *   - codebuddy / workbuddy / gemini：仅删除 ~/.{client}/skills/<name> 这个 junction / symlink，
 *     绝不删除真实仓库目录（依赖 isLink 安全判定）。
 *   - claude：删除项目级 .claude/commands/baskreport.md（提示：该文件可能被 git 跟踪）。
 *   - codex：删除项目级 AGENTS.md（提示：该文件可能被 git 跟踪）。
 *   - cursor：删除项目级 .cursor/rules/basksoft-ai.mdc（提示：该文件可能被 git 跟踪）。
 */
export function runUninstall(opts: Options, skillRoot: string): void {
  const skillName = path.basename(skillRoot);
  console.log(`Skill 名称: ${skillName}`);
  console.log(`仓库路径:   ${skillRoot}`);
  console.log(`目标客户端: ${opts.clients.join(", ")}`);

  const homeDir = os.homedir();

  console.log("\n[1/2] 移除客户端链接 / 命令与规则...");
  for (const client of opts.clients) {
    if (client === "claude") {
      const root = opts.projectRoot ?? skillRoot;
      const out = path.join(root, ".claude", "commands", "baskreport.md");
      removeFile(out, "claude 命令文件");
      continue;
    }
    if (client === "cursor") {
      const root = opts.projectRoot ?? skillRoot;
      const out = path.join(root, ".cursor", "rules", "basksoft-ai.mdc");
      removeFile(out, "cursor 规则文件");
      continue;
    }
    if (client === "generic") {
      const root = opts.projectRoot ?? skillRoot;
      removeFile(path.join(root, "baskreport.md"), "generic 通用 skill 文件");
      continue;
    }
    if (client === "codex") {
      const root = opts.projectRoot ?? skillRoot;
      removeFile(path.join(root, "AGENTS.md"), "codex 项目指令文件");
      continue;
    }

    // codebuddy / workbuddy / gemini：用户级目录链接（~/.{client}/skills）
    const skillsDir = path.join(homeDir, `.${client}`, "skills");
    const linkPath = path.join(skillsDir, skillName);

    if (!fs.existsSync(linkPath)) {
      console.log(`  不存在（跳过）: ${linkPath}`);
      continue;
    }

    if (isLink(linkPath)) {
      console.log(`  移除链接: ${linkPath}`);
      fs.rmSync(linkPath, { force: true, recursive: true });
    } else {
      throw new Error(
        `目标已存在且为真实目录，已中止以免误删数据: ${linkPath}\n请手动处理后再运行。`,
      );
    }
  }

  console.log("\n[2/2] 完成。对应客户端重新加载后将不再识别该 skill。");
}

function removeFile(target: string, label: string): void {
  if (!fs.existsSync(target)) {
    console.log(`  ${label}不存在（跳过）: ${target}`);
    return;
  }
  fs.rmSync(target, { force: true });
  console.log(`  已删除: ${target}`);
  console.log(
    `    （提示：${label}可能为 git 跟踪文件，必要时请手动 \`git rm\` 并提交。）`,
  );
}

// 作为 postinstall 脚本（node dist/install.js）直接运行时：
//   - 不依赖项目仓库，仅挂用户级 codebuddy/workbuddy
//   - skillRoot 指向包自身目录（dist 的上一级 = 包根）
// 作为 postinstall 脚本（node dist/install.js）直接运行时：
//   - 不依赖项目仓库，仅挂用户级 codebuddy/workbuddy
//   - skillRoot 指向包自身目录（dist 的上一级 = 包根）
// 判断依据：本模块文件本身是否就是 node 的入口脚本（文件名 = install.js）。
const currentFile = fileURLToPath(import.meta.url);
const invokedFile = process.argv[1] ? path.resolve(process.argv[1]) : "";
if (invokedFile && path.basename(currentFile) === "install.js" && currentFile === invokedFile) {
  const skillRoot = path.resolve(path.dirname(currentFile), "..");
  const opts = parseArgs(process.argv.slice(2));
  runInstall(opts, skillRoot);
}
